import type { KyInstance } from "ky";
import { toSearchParams, type PaginatedRequest, type PaginatedResponse } from "./pagination.js";
import { TURNSTILE_TOKEN_HEADER } from "./turnstile.js";

/** Where a practitioner has got to with a contact request. Stored, moved by the practitioner. */
export type InquiryStatus = "new" | "read" | "archived";

export type InquirySortKey = "createdAt" | "fullName" | "status";

/** A chosen value list entry: the slug the client sends, the label it renders. */
export interface InquirySelection {
  slug: string;
  label: string;
}

/**
 * Body of `POST /practitioners/:username/inquiries`. `issues` are `specialty` slugs and
 * `sessionType` an `inquirySessionType` slug — both from `GET /field-values`.
 */
export interface CreateInquiryRequest {
  fullName: string;
  phone: string;
  email: string;
  issues: string[];
  sessionType: string;
  comment?: string;
}

export interface Inquiry {
  id: string;
  practitionerId: string;
  fullName: string;
  phone: string;
  email: string;
  comment: string | null;
  status: InquiryStatus;
  issues: InquirySelection[];
  sessionType: InquirySelection | null;
  /** Set once the retention job has removed `comment`, six months after the inquiry was made. */
  commentRedactedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListInquiriesRequest extends PaginatedRequest {
  sortBy?: InquirySortKey;
  status?: InquiryStatus;
}

export interface UpdateInquiryStatusRequest {
  status: InquiryStatus;
}

/** Backs the "N New" badges on the sidebar and the inbox heading. */
export interface UnreadInquiriesCount {
  count: number;
}

export class InquiryApi {
  constructor(private readonly http: KyInstance) { }

  /** Public — a directory visitor contacting a practitioner by their username. No session needed. */
  create = (username: string, body: CreateInquiryRequest, turnstileToken: string): Promise<Inquiry> =>
    this.http
      .post(`practitioners/${encodeURIComponent(username)}/inquiries`, {
        json: body,
        headers: { [TURNSTILE_TOKEN_HEADER]: turnstileToken },
      })
      .json<Inquiry>();

  /** Practitioner-only, and scoped to the caller — never another practitioner's inquiries. */
  list = (params: ListInquiriesRequest = {}): Promise<PaginatedResponse<Inquiry>> =>
    this.http
      .get("inquiries", { searchParams: toSearchParams({ ...params }) })
      .json<PaginatedResponse<Inquiry>>();

  get = (id: string): Promise<Inquiry> =>
    this.http.get(`inquiries/${encodeURIComponent(id)}`).json<Inquiry>();

  updateStatus = (id: string, body: UpdateInquiryStatusRequest): Promise<Inquiry> =>
    this.http.patch(`inquiries/${encodeURIComponent(id)}`, { json: body }).json<Inquiry>();

  unreadCount = (): Promise<UnreadInquiriesCount> =>
    this.http.get("inquiries/unread-count").json<UnreadInquiriesCount>();
}
